/**
 * Fetch wrapper that attaches the current Cognito ID token as a Bearer
 * header. Callers obtain the token from react-oidc-context's ``auth.user``
 * and pass it in.
 */

import { getRuntimeConfig } from "@/runtimeConfig";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
    public readonly errorCode?: string,
  ) {
    super(
      typeof body === "object" && body && "message" in body
        ? String((body as { message: unknown }).message)
        : `HTTP ${status}`,
    );
    this.name = "ApiError";
  }
}

/**
 * Turns a non-2xx response into the ``ApiError`` both ``apiCall`` and ``apiCallText`` throw: every
 * route of this API reports its errors as JSON (``{ error, message, ... }``), whether the route's
 * success body is JSON or not, so this classification is shared regardless of which one called it.
 */
function toApiError(response: Response, text: string): ApiError {
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }
  const errorCode =
    typeof parsed === "object" && parsed && "error" in parsed
      ? String((parsed as { error: unknown }).error)
      : undefined;
  return new ApiError(response.status, parsed, errorCode);
}

export interface ApiCallOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  token: string | null | undefined;
  signal?: AbortSignal;
  /** Extra headers — used by the production key on destructive routes. */
  headers?: Record<string, string>;
}

export async function apiCall<T>(
  path: string,
  { method = "GET", body, token, signal, headers: extraHeaders }: ApiCallOptions,
): Promise<T> {
  const base = getRuntimeConfig().apiBaseUrl.replace(/\/$/, "");
  const url = `${base}${path}`;

  const headers: Record<string, string> = {
    Accept: "application/json",
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (extraHeaders) Object.assign(headers, extraHeaders);

  const response = await fetch(url, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  if (!response.ok) throw toApiError(response, text);

  if (!text) return null as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    return text as T;
  }
}

/**
 * Like ``apiCall``, but for a route that answers a non-JSON body (a downloadable document) — the
 * SAML SP metadata XML today. Carries the same bearer token and the same error handling (a non-2xx
 * response is still parsed as JSON and thrown as an ``ApiError``, since every route in this API
 * reports errors that way, only the success body differs).
 */
export async function apiCallText(
  path: string,
  { token, signal }: Pick<ApiCallOptions, "token" | "signal">,
): Promise<string> {
  const base = getRuntimeConfig().apiBaseUrl.replace(/\/$/, "");
  const url = `${base}${path}`;

  const headers: Record<string, string> = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const response = await fetch(url, { method: "GET", headers, signal });
  const text = await response.text();

  if (!response.ok) throw toApiError(response, text);
  return text;
}

/** The server answered 404: the thing asked for does not exist (as opposed to "could not be read"). */
export function isNotFound(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

// Statuses another attempt cannot fix: the session (401: the token a query captured is the one
// that was refused), the permission (403) or the item (404) is gone.
const TERMINAL_STATUSES = [401, 403, 404];

/** The failure is final: asking again gets the same answer. */
export function isTerminalError(error: unknown): boolean {
  return error instanceof ApiError && TERMINAL_STATUSES.includes(error.status);
}

/**
 * TanStack's ``retry`` for every query: one more attempt after a failure another attempt could fix
 * (a timeout, a 5xx), never after a terminal one. Retrying a 404 only delays the page that says
 * "not found" by the back-off (and, in a tab that is not visible, until it is visible again).
 */
export function retryUnlessTerminal(failureCount: number, error: unknown): boolean {
  return failureCount < 1 && !isTerminalError(error);
}
